import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { FileSignature, ClipboardCheck, Calendar, Soup, Wrench, MessageSquare, Eraser, ArrowLeftRight, IndianRupee } from "lucide-react";
import CloudinaryUpload from "../CloudinaryUpload";

/* =========================================================
   LIFECYCLE TAB — Agreement signing, Check-in/out, Rent schedule
   ========================================================= */
export function LifecycleTab({ bookings, user }) {
  const [selected, setSelected] = useState(null);
  const paid = bookings.filter(b => b.payment_status === "paid");

  useEffect(() => { if (!selected && paid.length) setSelected(paid[0]); }, [paid, selected]);

  if (paid.length === 0) {
    return <div className="brut-card p-8 text-center" data-testid="lifecycle-empty">
      <div className="font-display font-black text-2xl">No active bookings yet</div>
      <p className="text-[#484848] mt-2">Complete a payment to unlock e-agreement, check-in checklist and rent schedule.</p>
    </div>;
  }

  return (
    <div data-testid="lifecycle-tab">
      <div className="flex flex-wrap gap-2 mb-5">
        {paid.map(b => (
          <button key={b.id} onClick={()=>setSelected(b)} className={`brut-tag ${selected?.id === b.id ? "lime" : "white"} cursor-pointer`} data-testid={`life-bk-${b.id}`}>
            {b.property_title?.slice(0, 28)}
          </button>
        ))}
      </div>
      {selected && (
        <div className="space-y-6">
          <AgreementCard booking={selected} user={user}/>
          <RentScheduleCard booking={selected} user={user}/>
          <CheckInCard booking={selected} user={user} kind="check_in"/>
          {user.role === "owner" && <CheckInCard booking={selected} user={user} kind="check_out"/>}
          {(user.role === "owner" || user.role === "admin" || user.role === "super_admin") && <DepositDiffCard booking={selected}/>}
        </div>
      )}
    </div>
  );
}

function DepositDiffCard({ booking }) {
  const [diff, setDiff] = useState(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([{ label: "", amount: "", reason: "" }]);
  const [existing, setExisting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/checkins/diff/${booking.id}`);
      setDiff(data);
    } catch { /* */ }
    try {
      const { data } = await api.get(`/checkins/deposit-deduct/${booking.id}`);
      if (data && data.id) setExisting(data);
    } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [booking.id]);

  const totalDeducted = items.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const remaining = Math.max((booking.deposit || 0) - totalDeducted, 0);

  const submit = async () => {
    const cleaned = items.filter(x => x.label && parseFloat(x.amount) > 0).map(x => ({ label: x.label, amount: parseFloat(x.amount), reason: x.reason }));
    if (cleaned.length === 0) { toast.error("Add at least one deduction"); return; }
    try {
      await api.post("/checkins/deposit-deduct", { booking_id: booking.id, items: cleaned, refund_remaining: true });
      toast.success("Deposit settlement recorded");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="brut-card p-5" data-testid="deposit-diff-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-display font-black text-2xl flex items-center gap-2"><ArrowLeftRight size={20}/> Check-in vs Check-out comparison</h3>
        {!existing && diff?.check_in && diff?.check_out && (
          <button className="brut-btn pink" onClick={()=>setOpen(!open)} data-testid="deposit-toggle">{open ? "Cancel" : "Settle deposit"}</button>
        )}
      </div>

      {!diff || (!diff.check_in && !diff.check_out) ? (
        <p className="text-sm text-[#484848]">Comparison appears once tenant has submitted both check-in and check-out checklists.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#ECFAF1] border-b-2 border-[#0F1F17]">
                <tr><th className="p-2 text-left">Item</th><th className="p-2 text-left">Move-in</th><th className="p-2 text-left">Move-out</th><th className="p-2 text-left">Changed?</th></tr>
              </thead>
              <tbody>
                {diff.diff.map((d, i) => (
                  <tr key={i} className={`border-b border-[#0F1F17]/20 ${d.changed ? "bg-[#FFEAF6]" : ""}`} data-testid={`diff-row-${i}`}>
                    <td className="p-2">{d.label}</td>
                    <td className="p-2"><span className={`brut-tag ${d.check_in_status === "ok" ? "lime" : "pink"} !text-[10px]`}>{d.check_in_status}</span></td>
                    <td className="p-2"><span className={`brut-tag ${d.check_out_status === "ok" ? "lime" : "pink"} !text-[10px]`}>{d.check_out_status}</span></td>
                    <td className="p-2 text-xs">{d.changed ? "⚠️ YES" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(diff.check_in?.photos?.length > 0 || diff.check_out?.photos?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#484848] mb-2">Move-in photos ({diff.check_in?.photos?.length || 0})</div>
                <div className="grid grid-cols-3 gap-1">
                  {(diff.check_in?.photos || []).map((p, i) => <img key={i} src={p} alt="" className="brut-border h-20 w-full object-cover"/>)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#484848] mb-2">Move-out photos ({diff.check_out?.photos?.length || 0})</div>
                <div className="grid grid-cols-3 gap-1">
                  {(diff.check_out?.photos || []).map((p, i) => <img key={i} src={p} alt="" className="brut-border h-20 w-full object-cover"/>)}
                </div>
              </div>
            </div>
          )}

          {existing && (
            <div className="brut-card p-4 mt-5 bg-[#ECFAF1]" data-testid="deduction-record">
              <div className="text-xs uppercase tracking-wider">Settlement recorded</div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div><div className="text-xs text-[#484848]">Deposit</div><div className="font-bold text-lg">₹{existing.deposit_amount?.toLocaleString()}</div></div>
                <div><div className="text-xs text-[#484848]">Deducted</div><div className="font-bold text-lg text-[#FF90E8]">₹{existing.total_deducted?.toLocaleString()}</div></div>
                <div><div className="text-xs text-[#484848]">Refunded</div><div className="font-bold text-lg text-[#2ED573]">₹{existing.remaining_refund?.toLocaleString()}</div></div>
              </div>
              <ul className="mt-3 text-sm">
                {(existing.items || []).map((x, i) => <li key={i} className="border-b border-[#0F1F17]/20 py-1 flex justify-between"><span>{x.label} <span className="text-xs text-[#484848]">— {x.reason}</span></span><strong>₹{x.amount}</strong></li>)}
              </ul>
            </div>
          )}

          {open && !existing && (
            <div className="brut-card p-4 mt-5 bg-white" data-testid="deduction-form">
              <h4 className="font-bold">Itemize deductions (deposit ₹{booking.deposit || 0})</h4>
              <div className="space-y-2 mt-3">
                {items.map((it, i) => (
                  <div key={i} className="flex flex-wrap gap-2">
                    <input className="brut-input flex-1 min-w-[120px]" placeholder="What's damaged?" value={it.label} onChange={(e)=>{const x=[...items]; x[i].label=e.target.value; setItems(x);}} data-testid={`ded-label-${i}`}/>
                    <input className="brut-input w-28" type="number" placeholder="₹ Amount" value={it.amount} onChange={(e)=>{const x=[...items]; x[i].amount=e.target.value; setItems(x);}} data-testid={`ded-amt-${i}`}/>
                    <input className="brut-input flex-1 min-w-[120px]" placeholder="Reason / evidence" value={it.reason} onChange={(e)=>{const x=[...items]; x[i].reason=e.target.value; setItems(x);}}/>
                  </div>
                ))}
                <button className="brut-tag white cursor-pointer" onClick={()=>setItems([...items, { label: "", amount: "", reason: "" }])}>+ Add item</button>
              </div>
              <div className="flex justify-between items-center mt-4 brut-border p-3 bg-[#ECFAF1]">
                <div className="text-sm">
                  <div>Total deducted: <strong>₹{totalDeducted}</strong></div>
                  <div>Refund remaining: <strong className="text-[#2ED573]">₹{remaining}</strong></div>
                </div>
                <button className="brut-btn" onClick={submit} data-testid="deduction-submit"><IndianRupee size={14}/> Confirm settlement</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AgreementCard({ booking, user }) {
  const [agreement, setAgreement] = useState(null);
  const [fullName, setFullName] = useState(user?.name || "");
  const [accepted, setAccepted] = useState({});
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSig, setHasSig] = useState(false);

  useEffect(() => {
    api.post(`/agreements/init/${booking.id}`).then(r => {
      setAgreement(r.data);
      if (r.data.clauses) setAccepted(Object.fromEntries(r.data.clauses.map((_, i) => [i, false])));
    }).catch(() => {});
  }, [booking.id]);

  const startDraw = (e) => {
    drawing.current = true;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2; ctx.strokeStyle = "#0F1F17"; ctx.lineCap = "round";
    const r = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current; const ctx = c.getContext("2d");
    const r = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
    ctx.lineTo(x, y); ctx.stroke();
    setHasSig(true);
  };
  const stopDraw = () => { drawing.current = false; };
  const clearSig = () => {
    const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); setHasSig(false);
  };
  const submitSign = async () => {
    if (!hasSig) { toast.error("Please draw your signature"); return; }
    if (!fullName.trim()) { toast.error("Full name required"); return; }
    const allAccepted = agreement?.clauses?.every((_, i) => accepted[i]);
    if (!allAccepted) { toast.error("Please tick every clause to accept"); return; }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    try {
      const { data } = await api.post("/agreements/sign", {
        booking_id: booking.id, full_name: fullName, signature_data: dataUrl,
        accepted_clauses: agreement.clauses.filter((_, i) => accepted[i]),
      });
      setAgreement(data);
      toast.success("Agreement signed!");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  if (!agreement) return null;
  const signed = agreement.status === "signed";

  return (
    <div className="brut-card p-5" data-testid="agreement-card">
      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
        <h3 className="font-display font-black text-2xl flex items-center gap-2"><FileSignature size={20}/> Digital agreement</h3>
        <span className={`brut-tag ${signed ? "lime" : "pink"}`}>{signed ? "Signed" : "Pending signature"}</span>
      </div>
      <div className="space-y-2 mb-4">
        {agreement.clauses?.map((c, i) => (
          <label key={i} className="flex items-start gap-2 text-sm">
            <input type="checkbox" disabled={signed} checked={signed || !!accepted[i]} onChange={(e)=>setAccepted({...accepted, [i]: e.target.checked})} data-testid={`clause-${i}`}/>
            <span>{c}</span>
          </label>
        ))}
      </div>

      {signed ? (
        <div className="brut-border p-3 bg-[#ECFAF1]">
          <div className="text-xs uppercase tracking-wider text-[#484848]">Signed by</div>
          <div className="font-bold">{agreement.signed_by_name}</div>
          <div className="text-xs text-[#484848]">{agreement.signed_at?.slice(0, 19).replace("T", " ")}</div>
          {agreement.signature_data && <img src={agreement.signature_data} alt="signature" className="mt-2 brut-border bg-white max-h-32"/>}
        </div>
      ) : user.role === "student" ? (
        <div>
          <label className="label-eyebrow">Full legal name</label>
          <input className="brut-input mt-1 mb-3" value={fullName} onChange={(e)=>setFullName(e.target.value)} data-testid="sign-fullname"/>
          <label className="label-eyebrow">Draw your signature</label>
          <canvas
            ref={canvasRef} width={500} height={150}
            className="brut-border bg-white w-full mt-1 touch-none cursor-crosshair"
            onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
            onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={stopDraw}
            data-testid="sig-canvas"
          />
          <div className="flex gap-2 mt-3">
            <button className="brut-btn white !py-2 !px-3" onClick={clearSig}><Eraser size={14}/> Clear</button>
            <button className="brut-btn flex-1" onClick={submitSign} data-testid="agreement-sign-btn">Sign & submit</button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#484848]">Waiting for tenant to sign.</p>
      )}
    </div>
  );
}

function RentScheduleCard({ booking, user }) {
  const [items, setItems] = useState([]);
  const refresh = () => {
    api.post(`/rent/schedule/init/${booking.id}`).then(r => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { refresh(); }, [booking.id]);
  const markPaid = async (sid) => {
    try { await api.post("/rent/mark-paid", { schedule_id: sid, paid_via: "offline", reference: "" }); toast.success("Marked paid"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const downloadReceipt = async (sid) => {
    try {
      const r = await api.get(`/rent/receipt/${sid}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `hostro-receipt-${sid.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed"); }
  };
  const downloadLedger = async () => {
    try {
      const r = await api.get(`/bookings/${booking.id}/ledger`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `hostro-ledger-${booking.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed"); }
  };
  return (
    <div className="brut-card p-5" data-testid="rent-schedule-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-display font-black text-2xl flex items-center gap-2"><Calendar size={20}/> Rent schedule</h3>
        <button className="brut-btn white !py-1 !px-3 !text-xs" onClick={downloadLedger} data-testid="download-ledger-btn">Download full ledger PDF</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#ECFAF1] border-b-2 border-[#0F1F17]"><tr><th className="p-2 text-left">Month</th><th className="p-2 text-left">Due</th><th className="p-2 text-left">Amount</th><th className="p-2 text-left">Status</th><th className="p-2 text-right">Action</th></tr></thead>
          <tbody>
            {items.map(r => (
              <tr key={r.id} className="border-b border-[#0F1F17]/20" data-testid={`schedule-${r.id}`}>
                <td className="p-2">#{r.month_index}</td>
                <td className="p-2">{r.due_date}</td>
                <td className="p-2">₹{r.amount?.toLocaleString()}</td>
                <td className="p-2"><span className={`brut-tag ${r.status === "paid" ? "lime" : "pink"} !text-[10px]`}>{r.status}</span></td>
                <td className="p-2 text-right space-x-1">
                  {r.status === "paid" && (
                    <button className="brut-btn white !py-1 !px-2 !text-xs" onClick={()=>downloadReceipt(r.id)} data-testid={`receipt-${r.id}`}>Receipt PDF</button>
                  )}
                  {r.status !== "paid" && (user.role === "owner" || user.role === "admin") && (
                    <button className="brut-btn !py-1 !px-2 !text-xs" onClick={()=>markPaid(r.id)}>Mark paid</button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-[#484848]">Schedule will appear once booking is confirmed.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#484848] mt-2">Auto reminders: WhatsApp + Email at T-3 days, due date, and T+3 days overdue (daily 09:00 IST cron).</p>
    </div>
  );
}

function CheckInCard({ booking, user, kind }) {
  const [items, setItems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [existing, setExisting] = useState([]);

  useEffect(() => {
    api.get("/checkins/template").then(r => {
      setItems(r.data.items.map(i => ({ ...i, notes: "" })));
    }).catch(()=>{});
    api.get(`/checkins/booking/${booking.id}`).then(r => setExisting(r.data)).catch(()=>{});
  }, [booking.id]);

  const submit = async () => {
    try {
      await api.post("/checkins", { booking_id: booking.id, kind, items, photos });
      toast.success(`${kind === "check_in" ? "Check-in" : "Check-out"} submitted`);
      api.get(`/checkins/booking/${booking.id}`).then(r => setExisting(r.data));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const filed = existing.find(e => e.kind === kind);
  return (
    <div className="brut-card p-5" data-testid={`${kind}-card`}>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
        <h3 className="font-display font-black text-2xl flex items-center gap-2"><ClipboardCheck size={20}/> {kind === "check_in" ? "Move-in checklist" : "Move-out checklist"}</h3>
        {filed && <span className="brut-tag lime">Submitted {filed.created_at?.slice(0, 10)}</span>}
      </div>
      {filed ? (
        <div className="space-y-2">
          {filed.items?.map((it, i) => (
            <div key={i} className="text-sm flex flex-wrap justify-between gap-2 brut-border p-2 bg-[#FDFCF8]">
              <span>{it.label}</span>
              <span className={`brut-tag ${it.status === "ok" ? "lime" : "pink"} !text-[10px]`}>{it.status}</span>
            </div>
          ))}
          {filed.photos?.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mt-3">
              {filed.photos.map((p, i) => <img key={i} src={p} alt="" className="brut-border w-full h-24 object-cover"/>)}
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 brut-border p-2">
                <div className="flex-1 text-sm">{it.label}</div>
                <select className="brut-input !py-1 !text-xs" value={it.status} onChange={(e)=>{
                  const x = [...items]; x[i] = { ...x[i], status: e.target.value }; setItems(x);
                }} data-testid={`chk-status-${i}`}>
                  <option value="ok">OK</option><option value="issue">Issue</option><option value="missing">Missing</option>
                </select>
                <input className="brut-input !py-1 !text-xs w-40" placeholder="notes" value={it.notes} onChange={(e)=>{
                  const x = [...items]; x[i] = { ...x[i], notes: e.target.value }; setItems(x);
                }}/>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <label className="label-eyebrow">Photos (proof)</label>
            <CloudinaryUpload folder="hostro/documents" multiple={true} value={photos} onChange={setPhotos} testId={`${kind}-photos`}/>
          </div>
          <button className="brut-btn mt-4" onClick={submit} data-testid={`${kind}-submit`}>Submit {kind === "check_in" ? "check-in" : "check-out"}</button>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   ZOLO TAB — Food, Room Service, Community
   ========================================================= */
export function ZoloTab({ bookings, user }) {
  const paid = bookings.filter(b => b.payment_status === "paid");
  const [foodSubs, setFoodSubs] = useState([]);
  const [rs, setRs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [section, setSection] = useState("food");

  const reload = () => {
    api.get("/food/subscriptions").then(r => setFoodSubs(r.data)).catch(() => {});
    api.get("/room-service").then(r => setRs(r.data)).catch(() => {});
    api.get("/community/posts").then(r => setPosts(r.data)).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  return (
    <div data-testid="zolo-tab">
      <div className="flex gap-2 mb-5">
        {[["food", "Food", <Soup key="f" size={14}/>], ["rs", "Room service", <Wrench key="r" size={14}/>], ["c", "Community", <MessageSquare key="c" size={14}/>]].map(([k, l, ic]) => (
          <button key={k} onClick={()=>setSection(k)} className={`brut-tag ${section === k ? "lime" : "white"} cursor-pointer flex items-center gap-1`} data-testid={`zolo-tab-${k}`}>{ic}{l}</button>
        ))}
      </div>
      {section === "food" && <FoodSection paid={paid} subs={foodSubs} refresh={reload} user={user}/>}
      {section === "rs" && <RoomServiceSection paid={paid} requests={rs} refresh={reload} user={user}/>}
      {section === "c" && <CommunitySection posts={posts} refresh={reload}/>}
    </div>
  );
}

function FoodSection({ paid, subs, refresh, user }) {
  const [bookingId, setBookingId] = useState(paid[0]?.id || "");
  const [plan, setPlan] = useState("veg");
  const [meals, setMeals] = useState(["breakfast", "lunch", "dinner"]);
  const sub = async () => {
    if (!bookingId) return;
    try { await api.post("/food/subscribe", { booking_id: bookingId, plan, meals, price_per_month: plan === "non_veg" ? 3500 : plan === "jain" ? 2800 : 2500 }); toast.success("Food plan saved"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const toggleMeal = (m) => setMeals(meals.includes(m) ? meals.filter(x => x !== m) : [...meals, m]);
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="brut-card p-5">
        <h3 className="font-display font-black text-2xl mb-3">Food subscription</h3>
        {paid.length === 0 ? <p className="text-sm text-[#484848]">Confirm a booking to enable food plans.</p> : (
          <>
            <label className="label-eyebrow">Booking</label>
            <select className="brut-input mt-1" value={bookingId} onChange={(e)=>setBookingId(e.target.value)} data-testid="food-booking">
              {paid.map(b => <option key={b.id} value={b.id}>{b.property_title}</option>)}
            </select>
            <label className="label-eyebrow mt-3 block">Plan</label>
            <select className="brut-input mt-1" value={plan} onChange={(e)=>setPlan(e.target.value)} data-testid="food-plan">
              <option value="veg">Veg · ₹2,500/mo</option>
              <option value="non_veg">Non-veg · ₹3,500/mo</option>
              <option value="jain">Jain · ₹2,800/mo</option>
              <option value="weekend_only">Weekend only · ₹1,200/mo</option>
              <option value="none">Cancel subscription</option>
            </select>
            <label className="label-eyebrow mt-3 block">Meals</label>
            <div className="flex gap-2 flex-wrap">
              {["breakfast", "lunch", "dinner"].map(m => (
                <button key={m} onClick={()=>toggleMeal(m)} className={`brut-tag cursor-pointer ${meals.includes(m) ? "lime" : "white"}`}>{m}</button>
              ))}
            </div>
            <button className="brut-btn mt-4 w-full" onClick={sub} data-testid="food-subscribe-btn">Save plan</button>
          </>
        )}
      </div>
      <div>
        <h3 className="font-display font-black text-2xl mb-3">Your plans</h3>
        <div className="space-y-2">
          {subs.length === 0 && <div className="brut-card p-6 text-center text-[#484848]">No active food subscription.</div>}
          {subs.map(s => (
            <div key={s.id} className="brut-card p-4 text-sm">
              <div className="flex justify-between"><strong className="capitalize">{s.plan.replace("_", " ")}</strong><span className="brut-tag lime !text-[10px]">{s.status}</span></div>
              <div className="text-xs text-[#484848] mt-1">{s.meals?.join(" · ")}</div>
              <div className="font-bold mt-1">₹{s.price_per_month}/month</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RoomServiceSection({ paid, requests, refresh, user }) {
  const [bookingId, setBookingId] = useState(paid[0]?.id || "");
  const [category, setCategory] = useState("cleaning");
  const [description, setDescription] = useState("");
  const create = async () => {
    if (!description.trim()) return;
    try { await api.post("/room-service", { booking_id: bookingId, category, description }); toast.success("Request created"); setDescription(""); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="brut-card p-5">
        <h3 className="font-display font-black text-2xl mb-3">Request room service</h3>
        {paid.length > 0 && (
          <>
            <label className="label-eyebrow">Booking</label>
            <select className="brut-input mt-1" value={bookingId} onChange={(e)=>setBookingId(e.target.value)}>
              {paid.map(b => <option key={b.id} value={b.id}>{b.property_title}</option>)}
            </select>
          </>
        )}
        <label className="label-eyebrow mt-3 block">Category</label>
        <select className="brut-input mt-1" value={category} onChange={(e)=>setCategory(e.target.value)} data-testid="rs-category">
          {["cleaning", "laundry", "maintenance", "ac_servicing", "pest_control", "other"].map(c => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
        </select>
        <label className="label-eyebrow mt-3 block">Describe the issue</label>
        <textarea className="brut-input mt-1 min-h-[80px]" value={description} onChange={(e)=>setDescription(e.target.value)} data-testid="rs-description"/>
        <button className="brut-btn mt-4 w-full" onClick={create} data-testid="rs-submit">Submit request</button>
      </div>
      <div>
        <h3 className="font-display font-black text-2xl mb-3">My requests</h3>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {requests.length === 0 && <div className="brut-card p-6 text-center text-[#484848]">No requests yet.</div>}
          {requests.map(r => (
            <div key={r.id} className="brut-card p-3 text-sm">
              <div className="flex justify-between gap-2"><strong className="capitalize">{r.category}</strong><span className="brut-tag white !text-[10px]">{r.status}</span></div>
              <p className="text-xs text-[#484848] mt-1">{r.description}</p>
              <div className="text-xs mt-1">{r.created_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CommunitySection({ posts, refresh }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category: "general" });
  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    try { await api.post("/community/posts", form); toast.success("Posted!"); setForm({ title: "", body: "", category: "general" }); setShowForm(false); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const like = async (pid) => {
    try { await api.post(`/community/posts/${pid}/like`); refresh(); } catch { /* */ }
  };
  return (
    <div data-testid="community-section">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display font-black text-2xl">Community board</h3>
        <button className="brut-btn" onClick={()=>setShowForm(!showForm)} data-testid="community-new-btn">{showForm ? "Cancel" : "New post"}</button>
      </div>
      {showForm && (
        <div className="brut-card p-5 mb-5">
          <label className="label-eyebrow">Category</label>
          <select className="brut-input mt-1" value={form.category} onChange={(e)=>setForm({...form, category: e.target.value})} data-testid="post-category">
            {["general", "roommate", "marketplace", "events", "lost_found"].map(c => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
          <label className="label-eyebrow mt-3 block">Title</label>
          <input className="brut-input mt-1" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} data-testid="post-title"/>
          <label className="label-eyebrow mt-3 block">Body</label>
          <textarea className="brut-input mt-1 min-h-[100px]" value={form.body} onChange={(e)=>setForm({...form, body: e.target.value})} data-testid="post-body"/>
          <button className="brut-btn mt-4 w-full" onClick={submit} data-testid="post-submit">Post</button>
        </div>
      )}
      <div className="space-y-3">
        {posts.length === 0 && <div className="brut-card p-6 text-center text-[#484848]">Be the first to post.</div>}
        {posts.map(p => (
          <div key={p.id} className="brut-card p-4" data-testid={`post-${p.id}`}>
            <div className="flex justify-between flex-wrap gap-2"><span className="brut-tag white !text-[10px] capitalize">{p.category?.replace("_", " ")}</span><span className="text-xs text-[#484848]">{p.created_at?.slice(0, 10)}</span></div>
            <h4 className="font-bold mt-2">{p.title}</h4>
            <p className="text-sm text-[#484848] mt-1 whitespace-pre-wrap">{p.body}</p>
            <div className="flex items-center gap-3 mt-3 text-xs">
              <span>by {p.author_name}</span>
              <button onClick={()=>like(p.id)} className="brut-tag pink cursor-pointer" data-testid={`post-like-${p.id}`}>♥ {p.likes?.length || 0}</button>
              <span>💬 {p.comments?.length || 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
